/**
 * eyeFollowRuntime — eye (and a share of neck) camera-tracking (ENG-1060).
 *
 * The face-graph `eyeFollow` node writes the sentinel `__eyefollow__` into the
 * evaluator output and stashes its config here during evaluation, exactly as
 * `headFollow` / `__headfollow__` does. Per frame, the renderer that owns the
 * avatar calls `update()` once with the direction to the camera, then spends the
 * result in whatever currency it speaks:
 *
 *   GLB (HeadModel)      → `applyEyeFollow()` writes ARKit EyeLook* morphs
 *   splat rig (OGSModel) → `arkitOverlay()` folds into the composed ARKit frame
 *   eye branch (OGSModel)→ `angles()` drives `gazeController.setYawPitch`
 *   neck, both           → `neckShare()` is added to the __headfollow__ weight
 *
 * **The neck share is not a second neck implementation.** The ticket's Unreal
 * behaviour is 30% neck / 70% eye, which is a split of ONE aim angle — so the
 * node's `neckWeight` scales the sentinel and hands it to `applyHeadFollow`,
 * reusing the chain distribution, the clip-wrap spike damping and the
 * body-relative yaw clamp that already exist there. Two implementations of
 * "rotate the neck toward the camera" could only drift apart.
 *
 * NO THREE import, deliberately: `update()` takes plain numbers so the whole
 * controller is importable under `node --test` (the same reason `headAim.js` was
 * split out of `headFollowRuntime`). Callers own the vector scratch.
 *
 * `update()` must be called AT MOST ONCE PER FRAME — it advances smoothing by
 * `delta`. Exactly one renderer is mounted per character (AvatarModel for a GLB
 * rig, OGSModel for a splat), so ownership is unambiguous.
 */

import { aimAngles, arkitEyeChannels, EYE_LOOK_CHANNELS } from './eyeAim.js'
import { debugState } from './debugState.js'

const DEG = Math.PI / 180

// Defaults: the 70/30 split is the ticket's Unreal parity. The yaw/pitch limits
// are `gazeLimits.ts`'s own DEFAULT_GAZE_LIMITS (35/25) so the ARKit path and a
// gaze-conditioned eye branch agree on the eye's range rather than each having
// its own idea of it. Smoothing is snappier and the deadband tighter than
// headFollow's — eyes are near-instant on a real face, and a 2° deadband on a
// 35° range reads as sticky.
//
// `maxYaw`/`maxPitch` and `yawRange`/`pitchRange` are DELIBERATELY TWO KNOBS, and
// separating them is the fix for a real UX trap: `maxYaw`/`maxPitch` CLAMP the aim
// angle (how far the eye may point), while `yawRange`/`pitchRange` CALIBRATE the
// angle→morph mapping (the aim angle that maps to a fully-deflected eye,
// `arkitEyeChannels`). They used to be one number, so a user who raised `maxPitch`
// to "remove the limit" silently divided every up/down morph by that same larger
// number and the eyes stopped moving vertically. `yawRange`/`pitchRange` default
// to `maxYaw`/`maxPitch` (see setConfig) so a graph authored before the split — or
// one that never touches the calibration — behaves exactly as it did.
const DEFAULT_CONFIG = {
  eyeWeight: 0.7,
  neckWeight: 0.3,
  maxYaw: 35,
  maxPitch: 25,
  yawRange: 35,
  pitchRange: 25,
  smoothing: 0.35,
  deadbandDeg: 1,
}

// Preallocated: nothing here allocates per frame.
const _angles = { yaw: 0, pitch: 0 }
const _channels = new Float32Array(52)

export const eyeFollowRuntime = {
  config: { ...DEFAULT_CONFIG },

  // Smoothed state. `weight` is the smoothed sentinel gate, so toggling the node
  // fades instead of snapping.
  // refYaw/refPitch are written by eyeFollowScene: the frame the aim was
  // measured FROM, kept here so a console can see it without extra plumbing.
  state: { yaw: 0, pitch: 0, weight: 0, refYaw: 0, refPitch: 0 },

  setConfig(data) {
    if (!data) return
    const c = this.config
    c.eyeWeight   = Number(data.eyeWeight   ?? DEFAULT_CONFIG.eyeWeight)
    c.neckWeight  = Number(data.neckWeight  ?? DEFAULT_CONFIG.neckWeight)
    c.maxYaw      = Number(data.maxYaw      ?? DEFAULT_CONFIG.maxYaw)
    c.maxPitch    = Number(data.maxPitch    ?? DEFAULT_CONFIG.maxPitch)
    // Calibration falls back to the CLAMP when unset — so a node authored before
    // the clamp/calibration split (only maxYaw/maxPitch) keeps its exact prior
    // behaviour, where the clamp doubled as the morph normalizer.
    c.yawRange    = Number(data.yawRange    ?? data.maxYaw   ?? DEFAULT_CONFIG.yawRange)
    c.pitchRange  = Number(data.pitchRange  ?? data.maxPitch ?? DEFAULT_CONFIG.pitchRange)
    c.smoothing   = Number(data.smoothing   ?? DEFAULT_CONFIG.smoothing)
    c.deadbandDeg = Number(data.deadbandDeg ?? DEFAULT_CONFIG.deadbandDeg)
  },

  /**
   * Advance the aim one frame.
   *
   * @param {number} dx direction from the eyes to the target (world, unnormalized ok)
   * @param {number} dy
   * @param {number} dz
   * @param {{yaw:number,pitch:number}} ref  the frame the eyes rest in — the
   *   HEAD's current world yaw/pitch when there is a head bone (so a neck that
   *   has already turned toward the target isn't double-counted), else the body's
   * @param {number} gate    `__eyefollow__` sentinel weight (0 = node off/unwired)
   * @param {number} delta   seconds since last frame
   */
  update(dx, dy, dz, ref, gate, delta) {
    const cfg = this.config
    const s = this.state
    const dt = delta > 0 ? delta : 1 / 60
    // Framerate-independent exponential follow; higher smoothing = snappier.
    const lerp = 1 - Math.exp(-cfg.smoothing * dt * 60)

    s.weight += ((gate > 1 ? 1 : gate < 0 ? 0 : gate) - s.weight) * lerp

    const maxYaw = cfg.maxYaw * DEG
    const maxPitch = cfg.maxPitch * DEG
    aimAngles(dx, dy, dz, ref, maxYaw, maxPitch, _angles)

    // Continuous follow, every frame. The Dead Zone is NOT applied here: it only
    // gates the start of a user camera drag, and is applied to the camera
    // direction before it reaches this function (eyeFollowScene →
    // grabDeadzone.js). A per-frame deadband made slow drags move the eyes in
    // little steps.
    s.yaw += (_angles.yaw - s.yaw) * lerp
    s.pitch += (_angles.pitch - s.pitch) * lerp

    // Mutate the declared object (debugState.js) rather than assigning a fresh
    // one — this runs every frame.
    const dbg = debugState.eyeFollow
    dbg.weight = s.weight
    dbg.yaw = s.yaw
    dbg.pitch = s.pitch
  },

  /** Smoothed body-relative aim, in RADIANS. The eye branch consumes this. */
  angles() {
    return this.state
  },

  /**
   * The share of the aim the NECK should carry, as a weight to add to
   * `__headfollow__`. Uses the smoothed gate so it fades with the node.
   */
  neckShare() {
    const w = this.state.weight * this.config.neckWeight
    return w > 1 ? 1 : w < 0 ? 0 : w
  },

  /** The eye share of the smoothed gate (0..1). */
  eyeShare() {
    const w = this.state.weight * this.config.eyeWeight
    return w > 1 ? 1 : w < 0 ? 0 : w
  },

  /**
   * The 8 ARKit EyeLook channel weights for the current aim, in a REUSED buffer
   * indexed by ARKit channel. Valid until the next call; copy anything kept.
   * Every other channel in the buffer stays 0 — this never speaks for blink,
   * squint or anything else.
   *
   * `share` defaults to eyeShare(), and the splat path uses that default too —
   * there `eyeWeight` acts as the DEFLECTION GAIN (its neck has no consumer,
   * so `neckWeight` simply goes unspent). Measured on isaac-v6: full-gate
   * deflection rails the eye controls at the declared-range edges — values the
   * decoders never saw — which reads as distorted lids/whites at steep camera
   * angles; 0.7 keeps clear eye contact without the railing.
   */
  arkitOverlay(share = this.eyeShare()) {
    const cfg = this.config
    // Normalize against the CALIBRATION range, not the clamp: `yawRange`/
    // `pitchRange` set which aim angle counts as a fully-deflected eye. The clamp
    // (maxYaw/maxPitch) has already bounded state.yaw/pitch in update().
    arkitEyeChannels(
      this.state.yaw, this.state.pitch,
      cfg.yawRange * DEG, cfg.pitchRange * DEG,
      share, _channels,
    )
    return _channels
  },

  /** Reset to rest — used on character switch so a stale aim can't carry over. */
  reset() {
    this.state.yaw = 0
    this.state.pitch = 0
    this.state.weight = 0
    // The reference frame goes too: the next character's head is somewhere else.
    this.state.refYaw = 0
    this.state.refPitch = 0
  },
}

if (typeof window !== 'undefined') {
  window.__EYE_FOLLOW__ = eyeFollowRuntime
}

/**
 * Apply the eye aim to GLB morph target influences. Mirrors `applyBlink` — an
 * ADDITIVE overlay written after the mixer and after any face-clip / LiveLink
 * morph write, so procedural gaze layers on top of whatever the clip did rather
 * than fighting it for ownership of the channel.
 *
 * @param {Array} meshes  THREE.Mesh list with morphTargetInfluences
 * @param {Object|null} mapping ARKit index → GLB morph index (AudioAnimationBuffer._mapping)
 * @param {number} gate  `__eyefollow__` sentinel weight (0 = off, early-out)
 */
export function applyEyeFollow(meshes, mapping, gate) {
  if (gate <= 0 || eyeFollowRuntime.state.weight <= 0.001) return
  const ch = eyeFollowRuntime.arkitOverlay()

  for (const mesh of meshes) {
    const inf = mesh.morphTargetInfluences
    if (!inf) continue
    for (const c of EYE_LOOK_CHANNELS) {
      const v = ch[c]
      if (v <= 0) continue
      // Without a mapping the ARKit index IS the morph index (same assumption
      // applyBlink makes for rigs whose morph order matches the ARKit order).
      const idx = mapping ? mapping[c] : c
      if (idx === undefined || idx === -1 || idx >= inf.length) continue
      inf[idx] = Math.min(1.0, (inf[idx] || 0) + v)
    }
  }
}
